import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Hooks
import { useToaster } from "../../../../hooks/useToasterContext/useToasterContext";

// Types
import { IFieldDetailsData } from "../../../../components/masterData/pendingRequestManager/fieldDetails/PendingFieldDetailsTypes";
import { EComplianceReportType } from "../../../../components/shared/commonTypes/commonTypes";

// API and AWS services
import { getApi } from "../../../../services/apiManager/ApiManager";
import { getMediaSignedData } from "../../../../services/awsManager/AwsManager";

// Dummy image
import fieldDetailsModalDummyImage from "../../../../assets/images/mapDummyImage.png";

// Multi-language support
import { t } from "i18next";

// Common data formatter
import {
  getFormattedApprovalStatus,
  getFormattedComplianceStatus,
  getFormattedCropStatus,
  getFormattedDate,
  getFormattedFloatValue,
  getFormattedOverlapStatus,
  navigateToGoogleMaps,
} from "../../../../components/shared/commonDataFormatter/CommonDataFormatter";

// Components
import CommonDrawer from "../../../../components/shared/commonDrawer/CommonDrawer";
import ComplianceReportModal from "../complianceReport/ComplianceReportModal";
import Loader from "../../../../components/layout/Loader";
import CropCycleStartusWiseTable from "./CropCycleStatusWiseTable";

// R7 — month grouping for the monthly yield/quota matrix table (two rows of six)
import { YIELD_MONTH_ROWS_HALVES } from "../fieldDetailsModal/FieldDetailsModalTypes";

// Field Details component
const FieldDetails: React.FC = () => {
  // State variables
  const [fieldData, setFieldData] = useState<IFieldDetailsData | null>(null);
  const [fieldImageUrl, setFieldImageUrl] = useState<string>(
    fieldDetailsModalDummyImage,
  );
  const [isComplianceModal, setIsComplianceModal] = useState(false);
  const [isRefreshData, setIsRefreshData] = useState(false);
  const navigate = useNavigate();

  const { setShowToaster } = useToaster();
  const [searchParams] = useSearchParams();

  // Get field ID
  const fieldId = searchParams.get("fieldId");

  // Get field details
  const getFieldDetails = async () => {
    try {
      const apiParams = {
        id: fieldId,
      };
      const response = await getApi("/v1/field/details", apiParams);
      if (response.status && response.data) {
        const sortedData = {
          ...response.data,
          activeCycles: [...(response.data.activeCycles || [])].sort((a, b) => {
            const dateDiff =
              new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
            if (dateDiff !== 0) return dateDiff;

            return (a.seqId ?? 0) - (b.seqId ?? 0);
          }),

          pastCycles: [...(response.data.pastCycles || [])].sort((a, b) => {
            const dateDiff =
              new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
            if (dateDiff !== 0) return dateDiff;

            return (a.seqId ?? 0) - (b.seqId ?? 0);
          }),
        };
        setFieldData(sortedData);
        if (response.data.mapFileId) {
          const mediaSignedData = await getMediaSignedData(
            response.data.mapFileId,
          );
          if (mediaSignedData && mediaSignedData.s3SignedUrl) {
            setFieldImageUrl(mediaSignedData.s3SignedUrl);
          } else {
            setFieldImageUrl(fieldDetailsModalDummyImage);
          }
        } else {
          setFieldImageUrl(fieldDetailsModalDummyImage);
        }
      }
    } catch (error) {
      setShowToaster(true, t("commonMessages.internalServerError"), "error");
    }
  };

  // Get field details
  useEffect(() => {
    getFieldDetails();
  }, [fieldId]);

  // Handle open compliance report drawer
  const handleOpenComplianceReport = (fieldId: string) => {
    setIsComplianceModal(true);
  };

  // Handle cancel compliance report drawer
  const handleCancelComplianceReport = () => {
    setIsComplianceModal(false);
  };

  // Format list data
  const formatData = (list: any[]) => {
    return list
      .slice()
      .sort((a, b) => {
        const dateDiff =
          new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
        if (dateDiff !== 0) return dateDiff;

        return (a.seqId ?? 0) - (b.seqId ?? 0);
      })
      .map((item) => ({
        id: item.id,
        seqId: item.seqId ?? "-",
        cropCycleName: item.cropCycleName ?? "-",
        startDate: item.startDate ? getFormattedDate(item.startDate) : "-",
        endDate: item.harvestDate ? getFormattedDate(item.harvestDate) : "-",
        cycleStatus: getFormattedCropStatus({ status: item.cycleStatus }),
        numberOfActivities: item.numberOfActivities ?? "-",
      }));
  };

  return (
    <Box
      p={2}
      className="FieldDetails"
      sx={{
        height: "calc(100vh - 110px)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {(!fieldData || !fieldImageUrl) && <Loader />}
      <Box mb={2} display={"flex"} justifyContent={"space-between"}>
        <Typography variant="h6" fontWeight="bold" color="farm.main">
          {t("fieldDetails.fieldDetails")}
        </Typography>
        <Button
          variant="outlined"
          sx={{
            borderColor: "risinblack.main",
            color: "risinblack.main",
          }}
          onClick={() => {
            navigate("/farmers?selectedActiveTab=ENTITY_FIELD");
          }}
        >
          {/* <Icon className="icon-arrow-left" /> */}
          {t("fieldDetails.back")}
        </Button>
      </Box>
      <Box p={2} className="card-bg-shdw-rdius">
        <Box display="flex" gap={3}>
          <Box sx={{ width: "300px", flexShrink: 0 }}>
            <Box
              sx={{
                width: "100%",
                height: "220px",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <img
                src={fieldImageUrl}
                alt="Field"
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  objectFit: "cover",
                }}
              />
            </Box>
            {/* Navigate moved below the image (left-aligned) to free up
                header space. */}
            <Box
              className="navigate-wrap"
              sx={{
                color: "farm.main",
                mt: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                navigateToGoogleMaps(fieldData, setShowToaster);
              }}
            >
              <span className="icon-navigation" />
              <a
                className="navigate-link"
                href="#"
                style={{
                  color: "inherit",
                }}
              >
                {t("fieldDetails.navigate")}
              </a>
            </Box>
          </Box>
          <Box flex={1} display="flex" flexDirection="column">
            <Typography variant="h6" fontWeight={600} color="risinblack" mb={1}>
              {fieldData?.name}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.fieldArea")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {getFormattedFloatValue(fieldData?.fieldArea ?? 0)}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.landType")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {fieldData?.landName}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.fieldIdentificationNumber")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {fieldData?.fieldIdentificationNumber ?? "-"}
                </Typography>
              </Grid>

              {/* Dealer + Vendor row, sits on the SECOND row of the
                  field info grid (no separate card, no border). The
                  labels render ALWAYS so the slot is preserved even
                  when the field has no dealer linkage (or the linked
                  dealer was deleted and the FK cascade cleared the
                  reference) — empty values fall back to "-". AA-1569
                  follow-up: "remove only data not the labels". */}
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.dealerId")}
                </Typography>
                {/* Dealer ID is a link (like Farmer ID) to the Dealer Details
                    page, opened for this field's dealer. Plain text when the
                    field has no dealer linkage. */}
                {fieldData?.dealer?.id ? (
                  <Typography
                    component="a"
                    href={`/dealerdetails?dealerId=${fieldData.dealer.id}`}
                    color="blue"
                  >
                    {fieldData.dealer.dealerId ?? "-"}
                  </Typography>
                ) : (
                  <Typography variant="body2" fontWeight={500}>
                    {fieldData?.dealer?.dealerId ?? "-"}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.dealerName")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {fieldData?.dealer?.dealerName ?? "-"}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.dealerBranch")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {fieldData?.branch?.name ?? "-"}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.vendorId")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {fieldData?.vendor?.dealerId ?? "-"}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.vendorName")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {fieldData?.vendor?.dealerName ?? "-"}
                </Typography>
              </Grid>

              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("farmerDetails.farmerId")}
                </Typography>
                <Typography
                  component="a"
                  href={`/FarmerDetails?farmerId=${fieldData?.farmerId}`}
                  color="blue"
                >
                  {fieldData?.farmerSeqId}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.approvalStatus")}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {getFormattedApprovalStatus({
                    status: fieldData?.approvalStatus as any,
                  })}
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.overlapStatus")}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {getFormattedOverlapStatus({
                    status: fieldData?.overlapStatus as any,
                  })}
                </Stack>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body2" color="risinblack">
                  {t("fieldDetails.complianceStatus")}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {getFormattedComplianceStatus({
                    status: fieldData?.complianceStatus as any,
                  })}
                </Stack>
              </Grid>

              {/* R7 — Monthly Yield / Quota distribution (Jan–Dec) on the left,
                  with the Compliance Report button pinned bottom-right of the
                  same row. */}
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-end"
                  flexWrap="wrap"
                  gap={2}
                  mt={2}
                >
                  {fieldData?.yieldSummary ? (
                    <Box>
                      <Typography
                        variant="body2"
                        color="risinblack"
                        sx={{ mb: 1 }}
                      >
                        {t("fieldDetails.monthlyYield")}
                      </Typography>
                      <Table
                        size="small"
                        sx={{
                          tableLayout: "fixed",
                          maxWidth: 720,
                          border: 1,
                          borderColor: "divider",
                          "& td, & th": { borderColor: "divider" },
                        }}
                      >
                        <TableBody>
                          {YIELD_MONTH_ROWS_HALVES.map((monthRow, rowIndex) => (
                            <Fragment key={rowIndex}>
                              <TableRow
                                sx={{
                                  backgroundColor: "agricolors.drawerheader",
                                }}
                              >
                                {monthRow.map((monthKey) => (
                                  <TableCell
                                    key={monthKey}
                                    align="center"
                                    sx={{ fontSize: "12px", fontWeight: 600 }}
                                  >
                                    {t(`yieldConfig.months.${monthKey}`)}
                                  </TableCell>
                                ))}
                              </TableRow>
                              <TableRow>
                                {monthRow.map((monthKey) => (
                                  <TableCell
                                    key={monthKey}
                                    align="center"
                                    sx={{ fontSize: "12px" }}
                                  >
                                    {getFormattedFloatValue(
                                      fieldData?.yieldSummary?.[monthKey] ?? 0,
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : (
                    <Box />
                  )}

                  <Button
                    variant="outlined"
                    sx={{ borderRadius: "8px", textTransform: "none" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenComplianceReport(fieldData?.id || "");
                    }}
                  >
                    {t("fieldDetails.complianceReport.complianceReport")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <CropCycleStartusWiseTable
          tableName="cropCycleStatusWiseList"
          entity={t("cropCycles.entity.cropCycles")}
          importApiEndPoint="/v1/crop-cycle/import"
          exportApiEndPoint="/v1/crop-cycle/export"
          exportAllApiEndPoint="/v1/crop-cycle/export/activity-responses"
          columns={[
            "seqId",
            "cropCycleName",
            "startDate",
            "endDate",
            "cycleStatus",
            "numberOfActivities",
          ]}
          buttonConfig={{
            label: "",
            onClick: () => {
              return;
            },
          }}
          formatData={formatData}
          refreshData={isRefreshData}
          rowClick={() => {
            // console.log("Clicked row ID:", id);
          }}
          isFilterBtn={true}
          fieldId={fieldId ?? ""}
        />
      </Box>
      <CommonDrawer
        open={isComplianceModal}
        handleClose={handleCancelComplianceReport}
        drawerWidth={600}
        sx={{
          "& .MuiDrawer-paper": {
            overflowY: "unset",
          },
        }}
      >
        <ComplianceReportModal
          handleClose={handleCancelComplianceReport}
          fieldId={fieldId ?? ""}
          entityType={EComplianceReportType.FIELD}
        />
      </CommonDrawer>
    </Box>
  );
};

export default FieldDetails;
